import type { Kpi } from "../lib/types";
import { useAuth } from "../contexts/AuthContext";
import { fmt, fmt1 } from "../lib/format";
import { getPreliminaryInfo } from "../lib/periods";
import { toneOfRatio, toneOfInverse, TONE_BG } from "../lib/tone";

const RETURNS_NORM = 20;

interface CardSpec {
  title: string;
  fact: string;
  plan?: string;
  bar: number;
  barClass: string;
  kpi: string;
  note?: string;
  /** только админ: сколько доставки вычтено из продаж */
  adminNote?: string;
}

export function StatsScreen({ kpi }: { kpi: Kpi }) {
  const { t, me, period, periodId, monthStatuses } = useAuth();
  // Доставку НП видит только админ (решение владельца 01.11.2026); менеджеру API это поле не отдаёт
  const deliveryNote = (v: number | null | undefined) => me?.is_admin && v ? t("delivery_note", { value: fmt(v) }) : undefined;
  const prelim = getPreliminaryInfo(period, periodId, monthStatuses);
  const totalKpi = kpi.totalKpiAvg;
  const status = totalKpi >= 100 ? t("kpi_status_over") : totalKpi >= 80 ? t("kpi_status_norm") : t("kpi_status_under");
  const statusSuffix = kpi.monthsCount > 1 ? t("kpi_status_avg_suffix") : "";

  const cards: CardSpec[] = [];

  const ppPct = kpi.pureSales.plan > 0 ? (kpi.pureSales.fact / kpi.pureSales.plan) * 100 : 0;
  cards.push({
    title: t("card_pure_sales"),
    fact: `${fmt(kpi.pureSales.fact)} грн`,
    plan: t("plan_label", { value: `${fmt(kpi.pureSales.plan)} грн` }),
    bar: Math.min(ppPct, 100),
    barClass: TONE_BG[toneOfRatio(ppPct / 100)],
    kpi: kpi.monthsCount > 1 ? t("kpi_value_avg", { value: fmt1(kpi.pureSales.kpiAvg) }) : t("kpi_value", { value: fmt1(kpi.pureSales.kpiAvg) }),
    note: t("total_sales_note", { value: fmt(kpi.totalSales) }),
    adminNote: deliveryNote(kpi.delivery),
  });

  const rPct = kpi.returns.factPct;
  cards.push({
    title: t("card_returns"),
    fact: `${fmt1(rPct)}%`,
    plan: t("norm_label", { value: RETURNS_NORM }),
    bar: Math.min((rPct / RETURNS_NORM) * 100, 100),
    barClass: TONE_BG[toneOfInverse(rPct, RETURNS_NORM)],
    kpi: t("kpi_value", { value: fmt1(kpi.returns.kpiAvg) }),
    note: t("returns_amount_note", { value: fmt(kpi.returns.amount) }),
  });

  cards.push({
    title: t("card_remarks"),
    fact: String(kpi.remarks.fact),
    plan: "норма ≤ 2",
    bar: kpi.remarks.fact === 0 ? 100 : Math.min((kpi.remarks.fact / 2) * 100, 100),
    barClass: TONE_BG[toneOfInverse(kpi.remarks.fact, 2)],
    kpi: t("kpi_value", { value: fmt1(kpi.remarks.kpiAvg) }),
  });

  cards.push({
    title: t("card_reprimands"),
    fact: String(kpi.reprimands.fact),
    plan: "норма 0",
    bar: kpi.reprimands.fact === 0 ? 100 : 0,
    barClass: TONE_BG[toneOfInverse(kpi.reprimands.fact, 0)],
    kpi: t("kpi_value", { value: fmt1(kpi.reprimands.kpiAvg) }),
  });

  cards.push({
    title: t("card_competence"),
    fact: `${fmt(kpi.competence.score)} баллов`,
    bar: Math.min((kpi.competence.score / 70) * 100, 100),
    barClass: TONE_BG[toneOfRatio(kpi.competence.score / 70)],
    kpi: t("kpi_value", { value: fmt1(kpi.competence.kpiAvg) }),
    note: t("competence_score_note"),
  });

  const deptFactPct = kpi.dept.plan > 0 ? (kpi.dept.fact / kpi.dept.plan) * 100 : 0;

  return (
    <section className="p-4 space-y-3">
      <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl p-4 border border-indigo-100">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-indigo-700">{t("kpi_label")}</div>
          {prelim && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{t("prelim_badge")}</span>}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-indigo-900">{fmt1(totalKpi)}</span>
          <span className="text-sm text-indigo-700">%</span>
        </div>
        <div className="text-xs text-slate-600 mt-1">{status}{statusSuffix}</div>
      </div>

      {cards.map((c, i) => (
        <div key={i} className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">{c.title}</div>
              <div className="text-xl font-bold text-slate-900 mt-0.5">{c.fact}</div>
              {c.plan && <div className="text-xs text-slate-500">{c.plan}</div>}
            </div>
            <div className="text-xs text-slate-500">{c.kpi}</div>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className={`h-full ${c.barClass}`} style={{ width: `${c.bar}%` }} />
          </div>
          {c.note && <div className="text-[10px] text-slate-500 mt-1.5">{c.note}</div>}
          {c.adminNote && <div className="text-[10px] text-slate-500">{c.adminNote}</div>}
        </div>
      ))}

      <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
        <div className="flex items-start justify-between mb-1">
          <div className="text-xs uppercase tracking-wide text-slate-500">{t("card_dept")}</div>
          <div className="text-xs text-slate-500">
            {kpi.monthsCount > 1 ? t("kpi_value_avg", { value: fmt1(kpi.dept.kpiAvg) }) : t("kpi_value", { value: fmt1(kpi.dept.kpiAvg) })}
          </div>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-xl font-bold text-slate-900">{fmt(kpi.dept.fact)} грн</span>
          <span className="text-xs text-slate-500">из {fmt(kpi.dept.plan)} грн</span>
        </div>
        <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
          <div className={`h-full ${TONE_BG[toneOfRatio(deptFactPct / 100)]}`} style={{ width: `${Math.min(deptFactPct, 100)}%` }} />
        </div>
        {deliveryNote(kpi.dept.delivery) && <div className="text-[10px] text-slate-500 mt-1">{deliveryNote(kpi.dept.delivery)}</div>}
        <div className="text-[10px] text-slate-500 mt-1">{t("card_dept_note")}</div>
      </div>
    </section>
  );
}
